// tool-enable-disable-orchestrator.ts
//
// Orchestrate 1 lần enable/disable tool: đọc manifest → (kiểm runtime) → apply
// vào settings.json → cập nhật avatar-tools.json → log. Tách khỏi command để
// init/sync tái dùng đúng path enable (DRY).
//
// Fail-mềm: pack chưa có tools/ (version skew) → log warn + return false,
// KHÔNG throw (init không vỡ vì pack cũ).

import { spawnSync } from "node:child_process";
import {
  type ApplyToolResult,
  disableTool,
  enableTool,
  readToolManifest,
} from "./apply-tool-manifest-to-settings.js";
import { runAiOrchestratorSetupPicker } from "./setup-ai-orchestrator-model-tier-picker.js";
import { log } from "./terminal-logger.js";
import { readToolState, setToolState } from "./tool-state-store.js";

// Kiểm runtime manifest.requires (vd python3) có trên PATH. Chỉ cảnh báo, không chặn.
function warnIfRuntimeMissing(runtime?: string): void {
  if (!runtime) return;
  const probe = spawnSync(runtime, ["--version"], { stdio: "ignore" });
  if (probe.status !== 0 || probe.error) {
    log.warn(
      `Tool yêu cầu runtime '${runtime}' nhưng không tìm thấy trên PATH. ` +
        `Hook có thể fail khi chạy. Cài '${runtime}' rồi thử lại.`,
    );
  }
}

function logApplyResult(name: string, result: ApplyToolResult): void {
  switch (result.action) {
    case "enabled":
      log.success(`Tool '${name}' enabled (${result.changes.join("; ")}).`);
      if (result.backupPath) log.dim(`  Backup: ${result.backupPath}`);
      break;
    case "disabled":
      log.success(`Tool '${name}' disabled (${result.changes.join("; ")}).`);
      if (result.backupPath) log.dim(`  Backup: ${result.backupPath}`);
      break;
    case "no-change":
      log.dim(`Tool '${name}': settings.json đã đúng trạng thái, không thay đổi.`);
      break;
    case "no-manifest":
      break; // handled by caller
  }
}

// Enable 1 tool. Trả về true nếu manifest tồn tại (đã xử lý), false nếu pack
// chưa hỗ trợ (fail-mềm). `silent` = không in dòng "not supported" (dùng cho init
// khi tool default có thể chưa có trong pack cũ).
export async function enableToolByName(
  workspacePath: string,
  name: string,
  opts: { silent?: boolean; forceRefresh?: boolean } = {},
): Promise<boolean> {
  const manifest = await readToolManifest(workspacePath, name);
  if (!manifest) {
    if (!opts.silent) {
      log.warn(
        `Pack version hiện tại chưa hỗ trợ tool '${name}' (thiếu .claude/pack/tools/${name}/tool.json). Chạy 'avatar sync' để pull pack mới, hoặc kiểm tên tool.`,
      );
    }
    return false;
  }
  warnIfRuntimeMissing(manifest.requires?.runtime);

  // Special-case: ai-orchestrator cần setup-picker (chọn model cho 3 tier) TRƯỚC
  // khi merge manifest. Picker tự skip-and-return-true nếu state map đã tồn tại
  // (idempotent cho sync/re-enable). Trả false → skip enable hoàn toàn (vd key 1
  // model = route vô nghĩa, non-TTY, fetch fail, hoặc user hủy).
  if (name === "ai-orchestrator") {
    const ok = await runAiOrchestratorSetupPicker(workspacePath, {
      forceRefresh: opts.forceRefresh,
    });
    if (!ok) {
      return false; // KHÔNG merge manifest, KHÔNG set state enabled.
    }
    // ok → fallthrough merge manifest như thường.
  }

  const result = await enableTool(workspacePath, manifest);
  logApplyResult(name, result);
  await setToolState(workspacePath, name, { enabled: true, version: manifest.version });
  return true;
}

// Disable 1 tool. Đọc manifest để biết CHÍNH XÁC entry nào của tool.
export async function disableToolByName(workspacePath: string, name: string): Promise<boolean> {
  const manifest = await readToolManifest(workspacePath, name);
  if (!manifest) {
    log.warn(
      `Không tìm thấy manifest tool '${name}' để biết entry nào cần rút. Nếu pack đã đổi, state vẫn được set disabled nhưng settings.json có thể còn sót entry.`,
    );
    // Vẫn set state disabled để phản ánh ý định; settings không đổi (không có manifest).
    const state = await readToolState(workspacePath);
    const prevVersion = state.tools[name]?.version ?? "unknown";
    await setToolState(workspacePath, name, { enabled: false, version: prevVersion });
    return false;
  }
  const result = await disableTool(workspacePath, manifest);
  logApplyResult(name, result);
  await setToolState(workspacePath, name, { enabled: false, version: manifest.version });
  return true;
}
